import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';

import { fetchEULAThunk } from 'store/common/EULA/actions';
import { isScreenBelow767, isScreenBelow1004 } from 'store/common/settings/breakpoint/selectors';
import { fetchCheaperOptionTextsThunk } from 'store/ProductDetails/cheaperOption/actions';
import { loadDetails } from 'store/ProductDetails/common/actions';
import { FOLLOW_TABS_SCROLL_ID } from 'store/ProductDetails/common/constants';
import { isDetailsDeviceBestPricePromotionSelector } from 'store/ProductDetails/selectors';
import { postViewedProduct } from 'store/widgets/recentlyViewedProducts/actions';
import commonStyles from 'styles/common.pcss';
import Alternatives from 'pages/ProductDetails/views/_ViewLayout/components/CrossSell';
import RecommendationTabs from 'pages/ProductDetails/views/_ViewLayout/components/RecommendationTabs';

import { DetailsTakeawayPopup } from '../Simple/components';
import { CheaperModal, FollowTabs, TabRouter } from './components';
import FloatCard from './components/FloatCard/index';
import { SmartDevicePreview } from './components/SmartDevicePreview';
import styles from './styles.pcss';

const cx = classNames.bind({ ...styles, ...commonStyles });

const ViewLayout = ({ className, detailsType, extraChildren }) => {
  const dispatch = useDispatch();

  const [isFloatCard, setIsFloatCard] = useState(false);
  const [isOpenCheaperModal, setIsOpenCheaperModal] = useState(false);

  const scrollContainerRef = useRef(null);

  const { isBestPricePromotion, isMobile, isTablet } = useSelector(
    (state) => ({
      isBestPricePromotion: isDetailsDeviceBestPricePromotionSelector(state),
      isMobile: isScreenBelow767(state),
      isTablet: isScreenBelow1004(state),
    }),
    shallowEqual,
  );

  const onCloseCheaperModal = useCallback(() => {
    setIsOpenCheaperModal(false);
  }, []);

  const onOpenCheaperModal = useCallback(() => {
    setIsOpenCheaperModal(true);
  }, []);

  useEffect(() => {
    dispatch(postViewedProduct());
    dispatch(loadDetails(isMobile));
  }, [isMobile, dispatch]);

  let size = 'xl';

  if (isTablet) {
    size = 'm';
  }

  if (isMobile) {
    size = 's';
  }

  const handleScroll = () => {
    const isScrollBelow =
      // eslint-disable-next-line no-unsafe-optional-chaining
      scrollContainerRef?.current?.clientHeight +
        // eslint-disable-next-line no-unsafe-optional-chaining
        scrollContainerRef?.current?.getBoundingClientRect().top >
      0;
    const isScrollAbove = scrollContainerRef?.current?.getBoundingClientRect().top < 192;

    let shouldShowFloatCard = isScrollAbove;

    if (isMobile) {
      shouldShowFloatCard = isScrollAbove && isScrollBelow;
    }

    setIsFloatCard(shouldShowFloatCard);
  };

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  useEffect(() => {
    if (isBestPricePromotion) {
      dispatch(fetchCheaperOptionTextsThunk());
      dispatch(fetchEULAThunk());
    }
  }, [isBestPricePromotion]);

  return (
    <>
      <div
        className={cx('container', className, {
          'container--m': size === 'm',
          'container--s': size === 's',
          'container--xl': size === 'xl',
        })}
      >
        <SmartDevicePreview
          detailsType={detailsType}
          onOpenCheaperModal={onOpenCheaperModal}
          size={size}
        />
        {extraChildren}

        <div className={cx('fullWidth')}>
          <Alternatives />
        </div>
        <div className={cx('fullWidth')} id={FOLLOW_TABS_SCROLL_ID}>
          <FollowTabs />
          {/** this div fixes an issue with scrollContainer server renderer */}
          <div ref={scrollContainerRef}>
            <TabRouter />
          </div>
        </div>
        <div className={cx('fullWidth')}>
          <RecommendationTabs />
        </div>
        <DetailsTakeawayPopup />
      </div>

      {size !== 's' && (
        <div className={cx('fade-in', !isFloatCard && 'fade-out')}>
          <FloatCard />
        </div>
      )}
      <CheaperModal isOpen={isOpenCheaperModal} onClose={onCloseCheaperModal} />
    </>
  );
};

ViewLayout.propTypes = {
  className: PropTypes.string,
  detailsType: PropTypes.string,
  extraChildren: PropTypes.node,
};

export default ViewLayout;
